import type { FinancingInput, Quote } from '#shared/financing';

/** The vehicle fields the example needs; the calculator's vehicle fits. */
interface FinancingExampleVehicle {
  asking_price?: number | null;
  vat_deductible?: boolean | null;
}

export interface FinancingExampleRow {
  label: string;
  value: string;
  note?: string;
}

// Santander calculates with gross prices for everyone. Business customers see VAT-bearing amounts
// net, where the car's VAT is deductible; a loan's rates, interest and fees carry no VAT.
const VAT_FACTOR = 1.2;

const euroFormat = new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' });
const rateFormat = new Intl.NumberFormat('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

const formatEuro = (value: number) => euroFormat.format(value);
const formatKm = (km: number) => `${km.toLocaleString('de-AT')} km`;
const formatRate = (value: number) => `${rateFormat.format(value)} % p.a.`;

/**
 * The representative example of a quote, as the calculator and the inquiry
 * summary show it: its rows, and amounts net of VAT where that applies.
 */
export function useFinancingExample(
  vehicle: MaybeRefOrGetter<FinancingExampleVehicle>,
  input: MaybeRefOrGetter<FinancingInput>,
  quote: MaybeRefOrGetter<Quote | undefined>
) {
  const { t } = useI18n();

  const price = computed(() => Number(toValue(vehicle).asking_price ?? 0));
  const showNet = computed(() => toValue(input).customer === 'firma' && Boolean(toValue(vehicle).vat_deductible));
  const net = (amount: number) => (showNet.value ? amount / VAT_FACTOR : amount);

  const rows = computed<FinancingExampleRow[]>(() => {
    const example = toValue(quote);
    const terms = toValue(input);
    if (!example) return [];
    const k = (key: string, params?: Record<string, unknown>) => t(`components.financingCalculator.rows.${key}`, params ?? {});
    const term = { label: k('term'), value: k('termValue', { months: terms.termMonths, installments: example.installments }) };
    const nominal = {
      label: k(example.mode === 'kredit' && example.interestType === 'fix' ? 'nominalRateFixed' : 'nominalRateVariable'),
      value: formatRate(example.nominalRate),
    };
    const effective = { label: k('effectiveRate'), value: formatRate(example.effectiveRate) };
    /** An amount that includes VAT: net, with its "exkl. USt" label, for business customers. */
    const withVat = (key: string, amount: number) => ({ label: k(showNet.value ? `${key}Net` : key), value: formatEuro(net(amount)) });

    if (example.mode === 'kredit') {
      return [
        { label: k('monthlyRate'), value: formatEuro(example.monthlyRate) },
        withVat('cashPrice', price.value),
        { label: k('downPayment'), value: formatEuro(example.downPayment) },
        { label: k('totalLoanAmount'), value: formatEuro(example.totalLoanAmount) },
        term,
        ...(example.finalPayment > 0 ? [{ label: k('finalPayment'), value: formatEuro(example.finalPayment), note: k('finalPaymentNote') }] : []),
        nominal,
        effective,
        ...(example.processingFee != null ? [{ label: k('processingFee'), value: formatEuro(example.processingFee) }] : []),
        { label: k('totalCost'), value: formatEuro(example.totalCost) },
        { label: k('totalPayable'), value: formatEuro(example.totalPayable), note: k('totalPayableNote') },
      ];
    }
    const kmPerYear = terms.mode === 'leasing' ? terms.kmPerYear : 0;
    return [
      withVat('leaseRate', example.monthlyRate),
      withVat('cashPrice', price.value),
      withVat('leaseDownPayment', example.downPayment),
      withVat('financedAmount', price.value - example.downPayment),
      { label: k('depot'), value: formatEuro(example.depot) },
      term,
      { label: k('kmPerYear'), value: formatKm(kmPerYear), note: k('kmPerYearNote') },
      withVat('residualValue', example.residualValue),
      nominal,
      effective,
      { label: k('processingFee'), value: formatEuro(example.processingFee) },
      { label: k('legalFee'), value: formatEuro(example.legalFee) },
      { label: k('totalPayable'), value: formatEuro(example.totalPayable) },
    ];
  });

  return { price, showNet, net, rows, euro: formatEuro, formatKm };
}
